/**
 * Single source of truth for the SMCHS brand palette.
 *
 * Two design systems consume these values:
 *   - the public shell   (`.smchs-home`,   components/smchs/Shell.tsx)
 *   - the librarian app  (`.librarian-app`, components/librarian/LibrarianLayout.tsx)
 *
 * They deliberately differ in density and surface treatment, but the brand
 * primitives below are shared. Change the crest gold here and it changes in
 * both places. Do not hardcode these hex values anywhere else.
 */
export const BRAND = {
  /** Crest gold. The dark variant is lifted for legibility on navy. */
  gold: { light: '#C9A867', dark: '#D4B978' },
  goldSoft: '#E8D199',

  /**
   * Foreground for anything sitting ON a gold fill. Deliberately theme-invariant:
   * gold is a light colour in BOTH themes, so its foreground must stay dark in
   * both. Do not point this at `navy`: that token flips to gold on dark grounds,
   * which is what made every gold button render gold-on-gold (1.19:1).
   * navy on gold.light = 5.85:1 · navy on gold.dark = 6.95:1.
   */
  onGold: '#1E2D5C',

  /**
   * Gold as *type*. The crest gold is a 2:1 colour on parchment: a decoration
   * budget, not a text budget. This is the same hue (40deg) darkened to L=34%
   * so small labels, links and status text clear AA on light grounds.
   * light: 4.97:1 on parchment, 5.56:1 on paper. dark: gold.dark already
   * clears 7.3:1+ on every dark surface, so it passes straight through.
   * Use `gold` for rules, icon fills and backgrounds; `goldText` for text.
   */
  goldText: { light: '#80642D', dark: '#D4B978' },

  /**
   * School navy. On dark surfaces the true navy disappears, so the dark
   * variant is a lifted tint of the same hue: it keeps navy and gold as two
   * distinct accents in both themes instead of collapsing to one.
   */
  navy: { light: '#1E2D5C', dark: '#8FA9DC' },
  navySoft: { light: '#7E9BD4', dark: '#B9CBEE' },

  /** Text. `ink` and `paper` intentionally swap between themes. */
  ink: { light: '#0A1228', dark: '#F0EBE0' },
  paper: '#FFFFFF',

  /** Page grounds. */
  parchment: '#F5F2EA',
  deepNavy: '#0A1226',

  /**
   * Secondary text. It carries 10-11px mono labels, so it must clear AA on
   * every ground it sits on, and the raised surfaces are the binding
   * constraint, not the page: light `surface-2` (#ECE8DA) is darker than
   * parchment, dark `surface-2` (#1B2B4D) is lighter than deepNavy.
   * light: 5.06 parchment · 5.66 paper · 4.61 surface-2.
   * dark: 6.08 deepNavy · 5.24 surface · 4.57 surface-2.
   * Enforced by brand-tokens.test.ts; keep hierarchy ink > ink-soft > muted.
   */
  muted: { light: '#6B675A', dark: '#8A93AC' },

  /**
   * Boundary of an input, select or textarea in the staff app. The hairline
   * (#E8E4DC, 1.27:1 on white) is a divider, not a control edge: an empty field
   * drawn with it all but disappears. WCAG 1.4.11 asks 3:1 for the boundary.
   * light: 3.69 paper · 3.44 surface-2. dark: 3.91 surface · 3.58 surface-2.
   * Enforced by brand-tokens.test.ts.
   */
  fieldBorder: { light: '#8A8575', dark: '#6B7699' },

  status: {
    danger: { light: '#C2410C', dark: '#F97316' },
    success: { light: '#166534', dark: '#34D399' },
  },
} as const;
